import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatINR, formatDate, packageLabel, methodLabel } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "../components/ui/table";
import { ArrowLeft, Phone, MapPin, Wifi, Tv, Loader2, Receipt, Wallet } from "lucide-react";

export default function CustomerDetailPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/customers/${id}/transactions`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return <div className="text-gray-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  }
  if (!data) {
    return <div className="text-gray-500">Customer not found.</div>;
  }

  const { customer, bills, payments, totals } = data;
  const ServiceIcon = customer.service_type === "internet" ? Wifi : Tv;

  return (
    <div className="space-y-5">
      <div>
        <Link to="/customers" data-testid="back-to-customers" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-red-600">
          <ArrowLeft className="h-4 w-4" /> Back to customers
        </Link>
      </div>

      <Card className="p-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-md bg-red-50 text-red-600 grid place-items-center">
              <ServiceIcon className="h-6 w-6" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-gray-500">
                {customer.service_type === "internet" ? "Internet · User ID" : "Cable TV · Membership"} · {customer.customer_code}
              </div>
              <h1 className="text-3xl font-display font-bold mt-1">{customer.name}</h1>
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
                <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{customer.phone}</span>
                {customer.address && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{customer.address}</span>}
                <span>Package: <span className="font-medium text-gray-900">{packageLabel(customer.service_type, customer.package)}</span></span>
              </div>
              {customer.notes && <p className="mt-2 text-sm text-gray-500">{customer.notes}</p>}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 min-w-[300px]">
            <div className="text-right">
              <div className="text-xs uppercase tracking-wider text-gray-500">Billed</div>
              <div className="pds-stat-num text-xl mt-1">{formatINR(totals.total_billed)}</div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-wider text-gray-500">Paid</div>
              <div className="pds-stat-num text-xl mt-1 text-green-700">{formatINR(totals.total_paid)}</div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-wider text-gray-500">Outstanding</div>
              <div className={`pds-stat-num text-xl mt-1 ${totals.outstanding > 0 ? "text-red-700" : "text-gray-700"}`}>
                {formatINR(totals.outstanding)}
              </div>
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Receipt className="h-4 w-4 text-gray-500" />
          <h2 className="font-display font-semibold text-lg">Bills</h2>
        </div>
        <div className="overflow-x-auto">
          <Table data-testid="customer-bills-table">
            <TableHeader>
              <TableRow>
                <TableHead>Created</TableHead>
                <TableHead>Package</TableHead>
                <TableHead>Period</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-right">Paid</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Note</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {bills.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-gray-500 py-6">No bills yet.</TableCell></TableRow>
              )}
              {bills.map((b) => (
                <TableRow key={b.id}>
                  <TableCell className="text-sm">{formatDate(b.created_at)}</TableCell>
                  <TableCell>{packageLabel(b.service_type, b.package)}</TableCell>
                  <TableCell>{b.period_months} mo</TableCell>
                  <TableCell className="text-right tabular-nums">{formatINR(b.amount)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatINR(b.paid_amount)}</TableCell>
                  <TableCell>
                    <span className={`status-chip ${b.status === "paid" ? "status-paid" : "status-pending"}`}>
                      {b.status}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-gray-600 max-w-[220px] truncate" title={b.note}>{b.note || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Card className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Wallet className="h-4 w-4 text-gray-500" />
          <h2 className="font-display font-semibold text-lg">Payments</h2>
        </div>
        <div className="overflow-x-auto">
          <Table data-testid="customer-payments-table">
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Method</TableHead>
                <TableHead>Bill No</TableHead>
                <TableHead>Note</TableHead>
                <TableHead>Collected by</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payments.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-gray-500 py-6">No payments yet.</TableCell></TableRow>
              )}
              {payments.map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="text-sm">{formatDate(p.collected_at)}</TableCell>
                  <TableCell><span className="status-chip status-active">{methodLabel(p.method)}</span></TableCell>
                  <TableCell className="font-mono text-sm">{p.bill_no || <span className="text-gray-400">—</span>}</TableCell>
                  <TableCell className="text-sm text-gray-600 max-w-[260px] truncate" title={p.online_note}>{p.online_note || "—"}</TableCell>
                  <TableCell className="text-sm">{p.collected_by_name || "—"}</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold">{formatINR(p.amount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
